import React, { useState, useRef, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as XLSX from 'xlsx';
import {
  ArrowDownTrayIcon, ArrowUpTrayIcon, CheckCircleIcon,
  ExclamationCircleIcon, ExclamationTriangleIcon, XMarkIcon,
  DocumentTextIcon, UserGroupIcon, ShieldCheckIcon,
  WrenchScrewdriverIcon, BuildingOffice2Icon, ArrowPathIcon,
  InformationCircleIcon, ChevronDownIcon, ChevronUpIcon,
} from '@heroicons/react/24/outline';
import ApiURL from '../Api/Api';

/* ─── helpers ────────────────────────────────────────────────── */
function generatePassword(userName, mobile) {
  const prefix = (userName || '').replace(/[^a-zA-Z0-9]/g, '').slice(0, 3).toLowerCase();
  const suffix = (mobile || '').replace(/\D/g, '').slice(-3);
  return `${prefix}${suffix}`;
}

function parseCSV(text) {
  const lines = text.trim().split(/\r?\n/);
  const dataLines = lines.filter(l => !l.trim().startsWith('#') && l.trim() !== '');
  if (dataLines.length < 2) return { headers: [], rows: [] };
  const headers = dataLines[0].split(',').map(h => h.trim().toLowerCase().replace(/\s+/g, '_'));
  const rows = dataLines.slice(1).map((line, idx) => {
    const values = [];
    let current = '', inQuotes = false;
    for (let char of line) {
      if (char === '"') { inQuotes = !inQuotes; continue; }
      if (char === ',' && !inQuotes) { values.push(current.trim()); current = ''; continue; }
      current += char;
    }
    values.push(current.trim());
    const row = {};
    headers.forEach((h, i) => { row[h] = values[i] || ''; });
    row.__lineNum = idx + 2;
    return row;
  });
  return { headers, rows };
}

function normalizeHeader(header) {
  return String(header || '').trim().toLowerCase().replace(/\s+/g, '_');
}

function parseWorkbook(arrayBuffer) {
  const workbook = XLSX.read(arrayBuffer, { type: 'array' });
  const sheetName = workbook.SheetNames.find((name) => name.toLowerCase() !== 'instructions') || workbook.SheetNames[0];
  const sheet = workbook.Sheets[sheetName];
  const rowsAsArrays = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' })
    .filter((row) => row.some((cell) => String(cell).trim() !== ''));
  if (rowsAsArrays.length < 2) return { headers: [], rows: [] };
  const headers = rowsAsArrays[0].map(normalizeHeader);
  const rows = rowsAsArrays.slice(1).map((values, idx) => {
    const row = {};
    headers.forEach((header, i) => {
      row[header] = String(values[i] ?? '').trim();
    });
    row.__lineNum = idx + 2;
    return row;
  });
  return { headers, rows };
}

function validateRows(rows, siteOptions, remaining, availableRoles) {
  const validSiteNames = siteOptions.map(s => s.site_name.toLowerCase());
  const results = [];
  let usedTotal = 0, usedAdmin = 0, usedWorker = 0;
  const seenNames = new Set();

  rows.forEach(row => {
    const errors = [], warnings = [];
    const userName = (row.user_name || '').trim();
    const mobile   = (row.mobile_no  || '').trim();
    const role     = (row.role       || '').toLowerCase().trim();

    if (!userName) errors.push('Username is required');
    else if (userName.length < 3) errors.push('Username must be at least 3 characters');
    else if (seenNames.has(userName.toLowerCase())) errors.push('Duplicate username in this file');

    if (!mobile) errors.push('Mobile number is required');
    else if (mobile.replace(/\D/g, '').length < 7) errors.push('Mobile number invalid (need 7+ digits)');

    if (!role) errors.push('Role is required');
    else if (!['admin', 'worker'].includes(role)) errors.push(`Role must be "admin" or "worker", got "${row.role}"`);
    else if (!availableRoles.includes(role)) errors.push(`You cannot create "${role}" users`);

    const sitesRaw = row.assigned_sites || '';
    const siteList = sitesRaw ? sitesRaw.split(';').map(s => s.trim()).filter(Boolean) : [];
    const invalidSites = siteList.filter(s => !validSiteNames.includes(s.toLowerCase()));
    if (invalidSites.length > 0) errors.push(`Unknown site(s): ${invalidSites.join(', ')}`);

    const password = generatePassword(userName, mobile);
    if (password.length < 4) warnings.push('Generated password is very short');

    if (errors.length === 0) {
      usedTotal++;
      if (role === 'admin')  usedAdmin++;
      if (role === 'worker') usedWorker++;
      if (remaining) {
        if (remaining.users   !== null && usedTotal  > remaining.users)
          errors.push(`Exceeds total user limit (${remaining.users} slot${remaining.users !== 1 ? 's' : ''} left)`);
        else if (role === 'admin'  && remaining.admins  !== null && usedAdmin  > remaining.admins)
          errors.push(`Exceeds admin limit (${remaining.admins} slot${remaining.admins !== 1 ? 's' : ''} left)`);
        else if (role === 'worker' && remaining.workers !== null && usedWorker > remaining.workers)
          errors.push(`Exceeds worker limit (${remaining.workers} slot${remaining.workers !== 1 ? 's' : ''} left)`);
      }
    }

    if (userName) seenNames.add(userName.toLowerCase());
    results.push({ ...row, user_name: userName, mobile_no: mobile, role, siteList, password, errors, warnings, valid: errors.length === 0 });
  });
  return results;
}

function buildExampleCSV(siteOptions, availableRoles) {
  const siteNames = siteOptions.map(s => s.site_name).join(' | ');
  const rolesStr  = availableRoles.join(' | ');
  const s1 = siteOptions[0]?.site_name || 'Site A';
  const s2 = siteOptions[1]?.site_name || 'Site B';
  return [
    `# ============================================================`,
    `# BULK USER UPLOAD TEMPLATE`,
    `# ============================================================`,
    `# AVAILABLE ROLES    : ${rolesStr}`,
    `# AVAILABLE SITES    : ${siteNames || 'No sites configured'}`,
    `#`,
    `# COLUMN GUIDE`,
    `#   user_name      Required. Unique, min 3 chars. No spaces.`,
    `#   mobile_no      Required. Used to auto-generate password.`,
    `#   email_id       Optional.`,
    `#   role           Must be exactly: ${rolesStr}`,
    `#   assigned_sites Separate multiple sites with semicolon (;)`,
    `#`,
    `# PASSWORD FORMULA`,
    `#   First 3 alphanumeric chars of username (lowercase)`,
    `#   + Last 3 digits of mobile_no`,
    `#   Example: username=john_worker  mobile=9876543210`,
    `#            password = joh210`,
    `# ============================================================`,
    `user_name,mobile_no,email_id,role,assigned_sites`,
    `john_worker,9876543210,john@company.com,worker,${s1}`,
    `jane_admin,9123456780,jane@company.com,${availableRoles[0] || 'worker'},${s1};${s2}`,
    `bob_worker,9000000001,bob@company.com,worker,${s2}`,
  ].join('\n');
}

function buildExampleWorkbook(siteOptions, availableRoles) {
  const s1 = siteOptions[0]?.site_name || 'Site A';
  const s2 = siteOptions[1]?.site_name || 'Site B';
  const templateRows = [
    ['user_name', 'mobile_no', 'email_id', 'role', 'assigned_sites'],
    ['john_worker', '9876543210', 'john@company.com', 'worker', s1],
    ['jane_admin', '9123456780', 'jane@company.com', availableRoles[0] || 'worker', `${s1};${s2}`],
    ['bob_worker', '9000000001', 'bob@company.com', 'worker', s2],
  ];
  const instructionRows = [
    ['Bulk User Upload Template'],
    ['Available roles', availableRoles.join(' | ')],
    ['Available sites', siteOptions.map((s) => s.site_name).join(' | ') || 'No sites configured'],
    ['Password formula', 'first 3 alphanumeric chars of username + last 3 digits of mobile_no'],
    ['Multiple sites', 'Separate site names with semicolon (;)'],
    ['Important', 'Do not rename the headers in the Template sheet.'],
  ];
  const workbook = XLSX.utils.book_new();
  const templateSheet = XLSX.utils.aoa_to_sheet(templateRows);
  const instructionsSheet = XLSX.utils.aoa_to_sheet(instructionRows);
  templateSheet['!cols'] = [{ wch: 18 }, { wch: 15 }, { wch: 26 }, { wch: 12 }, { wch: 35 }];
  instructionsSheet['!cols'] = [{ wch: 22 }, { wch: 70 }];
  XLSX.utils.book_append_sheet(workbook, instructionsSheet, 'Instructions');
  XLSX.utils.book_append_sheet(workbook, templateSheet, 'Template');
  return workbook;
}

/* ─── Sub-components ─────────────────────────────────────────── */
const StatusBadge = ({ valid, errors }) => (
  valid
    ? <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-600 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5 whitespace-nowrap">
        <CheckCircleIcon className="w-3 h-3" />Valid
      </span>
    : <span className="inline-flex items-center gap-1 text-[10px] font-bold text-rose-600 bg-rose-50 border border-rose-200 rounded-full px-2 py-0.5 whitespace-nowrap">
        <ExclamationCircleIcon className="w-3 h-3" />{errors.length} error{errors.length > 1 ? 's' : ''}
      </span>
);

const PreviewRow = ({ row, idx }) => {
  const [expanded, setExpanded] = useState(false);
  const hasDetails = row.errors.length > 0 || row.warnings.length > 0;
  return (
    <div className={`border rounded-xl transition-all ${row.valid ? 'border-slate-200 bg-white' : 'border-rose-200 bg-rose-50/40'}`}>
      <button type="button" onClick={() => hasDetails && setExpanded(p => !p)}
        className={`w-full flex items-center gap-3 px-3 py-2.5 text-left ${hasDetails ? 'cursor-pointer' : 'cursor-default'}`}>
        <span className="text-[11px] font-bold text-slate-400 w-5 flex-shrink-0">#{idx + 1}</span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[12px] font-bold text-slate-700">{row.user_name || '—'}</span>
            {row.role && (
              <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-md ${row.role === 'admin' ? 'bg-violet-100 text-violet-700' : 'bg-emerald-100 text-emerald-700'}`}>
                {row.role}
              </span>
            )}
            <span className="text-[10px] text-slate-400 font-mono bg-slate-100 px-1.5 rounded">{row.password || '—'}</span>
          </div>
          {row.siteList?.length > 0 && (
            <p className="text-[10px] text-slate-400 mt-0.5 truncate">{row.siteList.join(', ')}</p>
          )}
        </div>
        <StatusBadge valid={row.valid} errors={row.errors} />
        {hasDetails && (expanded
          ? <ChevronUpIcon className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
          : <ChevronDownIcon className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
        )}
      </button>
      <AnimatePresence>
        {expanded && hasDetails && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
            <div className="px-4 pb-3 space-y-1.5 border-t border-slate-100 pt-2">
              {row.errors.map((e, i) => (
                <div key={i} className="flex items-start gap-1.5">
                  <ExclamationCircleIcon className="w-3.5 h-3.5 text-rose-500 flex-shrink-0 mt-0.5" />
                  <span className="text-[11px] text-rose-700">{e}</span>
                </div>
              ))}
              {row.warnings.map((w, i) => (
                <div key={i} className="flex items-start gap-1.5">
                  <ExclamationTriangleIcon className="w-3.5 h-3.5 text-amber-500 flex-shrink-0 mt-0.5" />
                  <span className="text-[11px] text-amber-700">{w}</span>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════ */
const BulkUserUpload = ({ siteOptions = [], remaining = null, availableRoles = ['worker'], onClose, onSuccess }) => {
  const companyId   = localStorage.getItem('company_id') || '';
  const creatorId   = localStorage.getItem('id') || '';
  const creatorRole = localStorage.getItem('role') || '';
  const createdBy   = localStorage.getItem('user_name') || '';

  const fileInputRef = useRef(null);
  const [parsedRows, setParsedRows]       = useState(null);
  const [fileName, setFileName]           = useState('');
  const [isDragging, setIsDragging]       = useState(false);
  const [isSubmitting, setIsSubmitting]   = useState(false);
  const [submitResults, setSubmitResults] = useState(null);

  const validRows   = useMemo(() => parsedRows?.filter(r => r.valid)  || [], [parsedRows]);
  const invalidRows = useMemo(() => parsedRows?.filter(r => !r.valid) || [], [parsedRows]);

  const handleFile = useCallback((file) => {
    if (!file) return;
    setFileName(file.name);
    setParsedRows(null);
    setSubmitResults(null);
    const isExcel = /\.(xlsx|xls)$/i.test(file.name);
    const reader = new FileReader();
    reader.onload = (e) => {
      const { rows } = isExcel ? parseWorkbook(e.target.result) : parseCSV(e.target.result);
      if (rows.length === 0) return;
      setParsedRows(validateRows(rows, siteOptions, remaining, availableRoles));
    };
    if (isExcel) {
      reader.readAsArrayBuffer(file);
    } else {
      reader.readAsText(file);
    }
  }, [siteOptions, remaining, availableRoles]);

  const onFileChange = (e) => { handleFile(e.target.files[0]); e.target.value = ''; };
  const onDrop = (e) => { e.preventDefault(); setIsDragging(false); handleFile(e.dataTransfer.files[0]); };

  const downloadExample = () => {
    const blob = new Blob([buildExampleCSV(siteOptions, availableRoles)], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = 'bulk_user_template.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  const downloadExcelExample = () => {
    const workbook = buildExampleWorkbook(siteOptions, availableRoles);
    XLSX.writeFile(workbook, 'bulk_user_template.xlsx');
  };

  const handleSubmit = async () => {
    if (validRows.length === 0) return;
    setIsSubmitting(true);
    try {
      const res = await fetch(`${ApiURL}/bulkcreateusers.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          creator_id: creatorId, company_id: companyId,
          creator_role: creatorRole, created_by: createdBy,
          users: validRows.map(r => ({
            user_name: r.user_name, mobile_no: r.mobile_no,
            email_id: r.email_id || '', role: r.role,
            assigned_sites: r.siteList || [],
          })),
        }),
      });
      const result = await res.json();
      if (result.status === 'error') throw new Error(result.message);

      const withPasswords = result.results.map(r => ({
        ...r,
        password: r.password || validRows.find(v => v.user_name === r.user_name)?.password || '',
      }));
      setSubmitResults(withPasswords);
      const successCount = withPasswords.filter(r => r.status === 'success').length;
      if (successCount > 0 && onSuccess) onSuccess(successCount);
    } catch (err) {
      setSubmitResults([{ status: 'error', user_name: 'Batch request', message: err.message || 'Request failed' }]);
    } finally {
      setIsSubmitting(false);
    }
  };

  const successCount = submitResults?.filter(r => r.status === 'success').length || 0;
  const failCount    = submitResults?.filter(r => r.status === 'error').length   || 0;

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-3 sm:p-4"
      onClick={(e) => e.target === e.currentTarget && onClose?.()}
    >
      <motion.div
        initial={{ opacity: 0, y: 40, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 20, scale: 0.97 }}
        transition={{ type: 'spring', damping: 26, stiffness: 300 }}
        className="w-full max-w-2xl max-h-[90vh] bg-white rounded-2xl overflow-hidden shadow-2xl flex flex-col"
      >
        {/* Header */}
        <div className="relative bg-slate-950 px-5 py-4 flex items-center gap-3 flex-shrink-0">
          <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-cyan-400 via-cyan-300 to-transparent" />
          <div className="w-9 h-9 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
            <UserGroupIcon className="w-5 h-5 text-cyan-400" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-widest text-cyan-400">Bulk Import</p>
            <h2 className="text-[15px] font-bold text-white">Upload Users via CSV</h2>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-xl bg-white/10 flex items-center justify-center text-slate-400 hover:text-white transition-colors">
            <XMarkIcon className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="px-5 py-4 space-y-4">

            {/* Password formula */}
            <div className="bg-cyan-50 border border-cyan-200 rounded-xl px-4 py-3 flex gap-3">
              <InformationCircleIcon className="w-4 h-4 text-cyan-600 flex-shrink-0 mt-0.5" />
              <div className="text-[11px] text-cyan-800 space-y-1">
                <p className="font-bold">Auto-generated password = first 3 chars of username + last 3 digits of mobile</p>
                <p>e.g. username <span className="font-mono bg-cyan-100 px-1 rounded">john_worker</span> + mobile <span className="font-mono bg-cyan-100 px-1 rounded">9876543210</span> → password <span className="font-mono bg-cyan-100 px-1 rounded font-bold">joh210</span></p>
              </div>
            </div>

            {/* Roles + Sites */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="bg-slate-50 border border-slate-200 rounded-xl px-3 py-3">
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">Available Roles</p>
                <div className="flex gap-2 flex-wrap">
                  {availableRoles.includes('admin') && (
                    <span className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-violet-100 border border-violet-200 text-[11px] font-bold text-violet-700">
                      <ShieldCheckIcon className="w-3 h-3" />admin
                    </span>
                  )}
                  {availableRoles.includes('worker') && (
                    <span className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-emerald-100 border border-emerald-200 text-[11px] font-bold text-emerald-700">
                      <WrenchScrewdriverIcon className="w-3 h-3" />worker
                    </span>
                  )}
                </div>
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-xl px-3 py-3">
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">Available Sites ({siteOptions.length})</p>
                <div className="flex gap-1.5 flex-wrap max-h-14 overflow-y-auto">
                  {siteOptions.map(s => (
                    <span key={s.site_id} className="flex items-center gap-1 px-2 py-1 rounded-lg bg-white border border-slate-200 text-[10px] font-semibold text-slate-600">
                      <BuildingOffice2Icon className="w-2.5 h-2.5" />{s.site_name}
                    </span>
                  ))}
                  {siteOptions.length === 0 && <span className="text-[11px] text-slate-400 italic">No sites configured</span>}
                </div>
              </div>
            </div>

            {/* Package slots */}
            {remaining && (
              <div className="bg-slate-900 rounded-xl px-4 py-3">
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2">Package Slots Remaining</p>
                <div className="flex gap-5">
                  {[
                    { label: 'Total',   val: remaining.users,   color: 'text-cyan-400'    },
                    { label: 'Admins',  val: remaining.admins,  color: 'text-violet-400'  },
                    { label: 'Workers', val: remaining.workers, color: 'text-emerald-400' },
                  ].map(({ label, val, color }) => (
                    <div key={label}>
                      <p className={`text-xl font-bold tabular-nums ${val === 0 ? 'text-rose-400' : color}`}>{val}</p>
                      <p className="text-[9px] text-slate-500 uppercase font-bold">{label}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Download template */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <button type="button" onClick={downloadExample}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border-2 border-dashed border-slate-300 hover:border-cyan-400 hover:bg-cyan-50 transition-all group">
                <div className="w-8 h-8 rounded-lg bg-slate-100 group-hover:bg-cyan-100 flex items-center justify-center transition-colors flex-shrink-0">
                  <ArrowDownTrayIcon className="w-4 h-4 text-slate-500 group-hover:text-cyan-600 transition-colors" />
                </div>
                <div className="text-left flex-1">
                  <p className="text-[12px] font-bold text-slate-700 group-hover:text-cyan-700 transition-colors">Download CSV Template</p>
                  <p className="text-[10px] text-slate-400">Works with direct CSV import</p>
                </div>
                <DocumentTextIcon className="w-4 h-4 text-slate-300 flex-shrink-0" />
              </button>
              <button type="button" onClick={downloadExcelExample}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border-2 border-dashed border-emerald-300 hover:border-emerald-500 hover:bg-emerald-50 transition-all group">
                <div className="w-8 h-8 rounded-lg bg-emerald-50 group-hover:bg-emerald-100 flex items-center justify-center transition-colors flex-shrink-0">
                  <ArrowDownTrayIcon className="w-4 h-4 text-emerald-600 transition-colors" />
                </div>
                <div className="text-left flex-1">
                  <p className="text-[12px] font-bold text-slate-700 group-hover:text-emerald-700 transition-colors">Download Excel Template</p>
                  <p className="text-[10px] text-slate-400">Includes readable instructions sheet</p>
                </div>
                <DocumentTextIcon className="w-4 h-4 text-emerald-300 flex-shrink-0" />
              </button>
            </div>

            {/* Drop zone */}
            {!submitResults && (
              <div
                onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={onDrop}
                onClick={() => fileInputRef.current?.click()}
                className={`relative cursor-pointer rounded-xl border-2 border-dashed transition-all py-8 flex flex-col items-center gap-3 ${
                  isDragging ? 'border-cyan-400 bg-cyan-50' : 'border-slate-200 bg-slate-50 hover:border-slate-400 hover:bg-white'
                }`}
              >
                <input ref={fileInputRef} type="file" accept=".csv,.xlsx,.xls" onChange={onFileChange} className="hidden" />
                <div className={`w-12 h-12 rounded-2xl flex items-center justify-center transition-colors ${isDragging ? 'bg-cyan-100' : 'bg-white border border-slate-200'}`}>
                  <ArrowUpTrayIcon className={`w-5 h-5 transition-colors ${isDragging ? 'text-cyan-600' : 'text-slate-400'}`} />
                </div>
                {fileName
                  ? <div className="text-center">
                      <p className="text-[13px] font-bold text-slate-700">{fileName}</p>
                      <p className="text-[11px] text-slate-400 mt-0.5">Click to replace</p>
                    </div>
                  : <div className="text-center">
                      <p className="text-[13px] font-semibold text-slate-600">Drop CSV here or <span className="text-cyan-600">click to browse</span></p>
                      <p className="text-[11px] text-slate-400 mt-0.5">Accepts .csv, .xlsx and .xls files</p>
                    </div>
                }
              </div>
            )}

            {/* Preview */}
            {parsedRows && !submitResults && (
              <div>
                <div className="flex items-center gap-3 mb-3 flex-wrap">
                  <p className="text-[12px] font-bold text-slate-600 flex-1">
                    {parsedRows.length} row{parsedRows.length !== 1 ? 's' : ''} found — <span className="text-emerald-600">{validRows.length} valid</span>
                    {invalidRows.length > 0 && <>, <span className="text-rose-600">{invalidRows.length} with errors</span></>}
                  </p>
                  {invalidRows.length > 0 && (
                    <span className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5 font-bold">
                      Only valid rows will be imported
                    </span>
                  )}
                </div>
                <div className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
                  {parsedRows.map((row, i) => <PreviewRow key={i} row={row} idx={i} />)}
                </div>
              </div>
            )}

            {/* Submitting */}
            {isSubmitting && (
              <div className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-xl px-4 py-4">
                <ArrowPathIcon className="w-5 h-5 text-cyan-500 animate-spin flex-shrink-0" />
                <div>
                  <p className="text-[13px] font-bold text-slate-700">Creating {validRows.length} users…</p>
                  <p className="text-[10px] text-slate-400">Single batch request — please wait</p>
                </div>
              </div>
            )}

            {/* Results */}
            {submitResults && (
              <div>
                <div className={`rounded-xl border px-4 py-3 mb-3 flex items-center gap-3 ${successCount > 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-rose-50 border-rose-200'}`}>
                  {successCount > 0
                    ? <CheckCircleIcon className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                    : <ExclamationCircleIcon className="w-5 h-5 text-rose-500 flex-shrink-0" />
                  }
                  <div>
                    <p className="text-[13px] font-bold text-slate-800">
                      {successCount} user{successCount !== 1 ? 's' : ''} created
                      {failCount > 0 && <>, <span className="text-rose-600">{failCount} failed</span></>}
                    </p>
                    <p className="text-[10px] text-slate-500 mt-0.5">Share passwords securely with each user</p>
                  </div>
                </div>
                <div className="space-y-1.5 max-h-56 overflow-y-auto">
                  {submitResults.map((r, i) => (
                    <div key={i} className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border text-[11px] ${r.status === 'success' ? 'border-slate-200 bg-white' : 'border-rose-200 bg-rose-50'}`}>
                      {r.status === 'success'
                        ? <CheckCircleIcon className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                        : <ExclamationCircleIcon className="w-3.5 h-3.5 text-rose-500 flex-shrink-0" />
                      }
                      <span className="font-bold text-slate-700 truncate max-w-[120px]">{r.user_name}</span>
                      {r.role && (
                        <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold flex-shrink-0 ${r.role === 'admin' ? 'bg-violet-100 text-violet-700' : 'bg-emerald-100 text-emerald-700'}`}>{r.role}</span>
                      )}
                      {r.status === 'success' && r.password && (
                        <span className="ml-auto font-mono text-slate-500 flex-shrink-0">pw: <span className="font-bold text-slate-800">{r.password}</span></span>
                      )}
                      {r.status === 'error' && (
                        <span className="ml-auto text-rose-600 text-right">{r.message}</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-slate-100 flex gap-2 flex-shrink-0 bg-white">
          {submitResults ? (
            <button onClick={onClose} className="w-full h-10 rounded-xl bg-slate-950 text-white text-[13px] font-bold hover:bg-slate-800 transition-colors">Done</button>
          ) : (
            <>
              <button onClick={onClose} className="flex-1 h-10 rounded-xl border border-slate-200 text-slate-600 text-[12px] font-bold hover:bg-slate-50 transition-colors">Cancel</button>
              <motion.button type="button" onClick={handleSubmit}
                disabled={isSubmitting || validRows.length === 0}
                whileTap={{ scale: 0.98 }}
                className="flex-[2] h-10 rounded-xl bg-slate-950 text-white text-[13px] font-bold hover:bg-slate-800 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isSubmitting
                  ? <><ArrowPathIcon className="w-4 h-4 animate-spin" />Importing…</>
                  : <><UserGroupIcon className="w-4 h-4 text-cyan-400" />Import {validRows.length} User{validRows.length !== 1 ? 's' : ''}</>
                }
              </motion.button>
            </>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default BulkUserUpload;
